<template>
  <teleport to="body">
    <transition name="modal">
      <div v-if="show" class="fixed inset-0 z-[9999] overflow-y-auto">
        <!-- Backdrop -->
        <div class="modal-backdrop" @click="closeModal"></div>

        <!-- Modal -->
        <div class="modal-container">
          <div class="modal-content max-w-3xl max-h-[90vh] sm:max-h-[85vh]" @click.stop>
            <!-- Header -->
            <div class="modal-header">
              <div>
                <h2 class="modal-title">Select Buyer</h2>
                <p class="text-base text-gray-500 mt-1">Choose a company from the list below</p>
              </div>
              <button @click="closeModal" class="btn-icon btn-secondary">
                <LineIcon name="x" :size="15" :stroke-width="1.8" class="text-lg" />
              </button>
            </div>

            <!-- Tabs -->
            <div class="modal-tabs">
              <button
                v-for="tab in tabs"
                :key="tab.id"
                @click="activeTab = tab.id"
                class="modal-tab"
                :class="{ 'active': activeTab === tab.id }"
              >
                {{ tab.label }}
              </button>
            </div>

            <!-- Content -->
            <div class="modal-body">
              <!-- Companies Tab -->
              <div v-if="activeTab === 'companies'">
                <!-- Search and Sort -->
                <div class="modal-search-container flex flex-col sm:flex-row gap-4">
                  <div class="modal-search-input-wrapper flex-1">
                    <input
                      v-model="companySearch"
                      type="text"
                      placeholder="Search companies..."
                      class="form-input pl-12"
                      @input="filterCompanies"
                    />
                  </div>
                  <div class="sm:w-56">
                    <SelectField v-model="companyOrderBy" :options="companyOrderByOptions" class="form-input appearance-none cursor-pointer" @change="filterCompanies" />
                  </div>
                </div>

                <div v-if="loadingCompanies" class="flex items-center justify-center py-16">
                  <div class="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-blue-600"></div>
                </div>

                <div v-else-if="filteredCompanies.length === 0" class="flex flex-col items-center justify-center py-16 text-center">
                  <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <LineIcon name="building" :size="15" :stroke-width="1.8" class="text-gray-400 text-2xl" />
                  </div>
                  <p class="text-gray-900 font-medium text-lg">No companies found</p>
                  <p class="text-base text-gray-500 mt-1">Try adjusting your search terms</p>
                </div>

                <div v-else class="space-y-3">
                  <button
                    v-for="company in filteredCompanies"
                    :key="company.id"
                    @click="selectCompany(company)"
                    class="modal-list-item"
                  >
                    <div class="modal-list-item-icon company">
                      <LineIcon name="building" :size="15" :stroke-width="1.8" />
                    </div>
                    <div class="modal-list-item-content">
                      <div class="modal-list-item-header">
                        <h3 class="modal-list-item-title">{{ company.name }}</h3>
                        <span v-if="company.vat_number" class="modal-list-item-badge">
                          {{ company.vat_number }}
                        </span>
                      </div>
                      <p v-if="company.address" class="modal-list-item-address">
                        <LineIcon name="map-pin" :size="15" :stroke-width="1.8" class="text-gray-400 text-xs mr-1" />
                        {{ company.address }}
                      </p>
                    </div>
                    <div class="modal-list-item-arrow">
                      <LineIcon name="arrow-right" :size="15" :stroke-width="1.8" />
                    </div>
                  </button>
                </div>
              </div>

              <!-- Vendors Tab -->
              <div v-if="activeTab === 'vendors'">
                <!-- Search and Filters -->
                <div class="modal-search-container grid grid-cols-1 sm:grid-cols-12 gap-4">
                  <div class="modal-search-input-wrapper sm:col-span-5">
                    <LineIcon name="search" :size="15" :stroke-width="1.8" class="modal-search-icon" />
                    <input
                      v-model="vendorSearch"
                      type="text"
                      placeholder="Search vendors..."
                      class="form-input pl-12"
                      @input="filterVendors"
                    />
                  </div>
                  <div class="sm:col-span-4">
                    <SelectField v-model="selectedCompanyId" :options="selectedCompanyIdOptions" class="form-input cursor-pointer" @change="loadVendorsForCompany" />
                  </div>
                  <div class="sm:col-span-3">
                    <SelectField v-model="vendorOrderBy" :options="vendorOrderByOptions" class="form-input appearance-none cursor-pointer" @change="filterVendors" />
                  </div>
                </div>

                <div v-if="loadingVendors" class="flex items-center justify-center py-16">
                  <div class="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-blue-600"></div>
                </div>

                <div v-else-if="filteredVendors.length === 0" class="flex flex-col items-center justify-center py-16 text-center">
                  <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <LineIcon name="users" :size="15" :stroke-width="1.8" class="text-gray-400 text-2xl" />
                  </div>
                  <p class="text-gray-900 font-medium text-lg">No vendors found</p>
                  <p class="text-base text-gray-500 mt-1">Try adjusting your search or filters</p>
                </div>

                <div v-else class="space-y-3">
                  <button
                    v-for="vendor in filteredVendors"
                    :key="vendor.id"
                    @click="selectVendor(vendor)"
                    class="modal-list-item"
                  >
                    <div class="modal-list-item-icon vendor">
                      <LineIcon name="user-tie" :size="15" :stroke-width="1.8" />
                    </div>
                    <div class="modal-list-item-content">
                      <div class="modal-list-item-header">
                        <h3 class="modal-list-item-title">{{ vendor.vendor_name }}</h3>
                        <span v-if="vendor.vendor_vat" class="modal-list-item-badge">
                          {{ vendor.vendor_vat }}
                        </span>
                        <span v-if="vendor.company" class="modal-list-item-badge company-badge">
                          <LineIcon name="building" :size="15" :stroke-width="1.8" class="text-xs mr-1" />
                          {{ vendor.company.name }}
                        </span>
                      </div>
                      <p v-if="vendor.vendor_address" class="modal-list-item-address">
                        <LineIcon name="map-pin" :size="15" :stroke-width="1.8" class="text-gray-400 text-xs mr-1" />
                        {{ vendor.vendor_address }}
                      </p>
                    </div>
                    <div class="modal-list-item-arrow">
                      <LineIcon name="arrow-right" :size="15" :stroke-width="1.8" />
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import SelectField from '@/components/SelectField.vue';
import LineIcon from '@/components/icons/LineIcon.vue';
import { ref, watch, computed, unref } from 'vue';
import api from '@/utils/api';

const props = defineProps({
  show: {
    type: Boolean,
    required: true
  }
});

const emit = defineEmits(['close', 'select']);

const activeTab = ref('companies');
const companies = ref([]);
const vendors = ref([]);
const selectedCompanyId = ref(null);
const loadingCompanies = ref(false);
const loadingVendors = ref(false);

// Search and sort
const companySearch = ref('');
const companyOrderBy = ref('name');
const vendorSearch = ref('');
const vendorOrderBy = ref('vendor_name');

const tabs = [
  { id: 'companies', label: 'Companies' },
  { id: 'vendors', label: 'Vendors' }
];

// Filtered and sorted companies
const filteredCompanies = computed(() => {
  let filtered = companies.value;

  // Apply search filter
  if (companySearch.value) {
    const search = companySearch.value.toLowerCase();
    filtered = filtered.filter(company =>
      company.name?.toLowerCase().includes(search) ||
      company.vat_number?.toLowerCase().includes(search) ||
      company.address?.toLowerCase().includes(search)
    );
  }

  // Apply sorting
  return filtered.sort((a, b) => {
    if (companyOrderBy.value === 'name') {
      return (a.name || '').localeCompare(b.name || '');
    } else if (companyOrderBy.value === 'vat_number') {
      return (a.vat_number || '').localeCompare(b.vat_number || '');
    } else if (companyOrderBy.value === 'created_at') {
      return new Date(b.created_at) - new Date(a.created_at);
    }
    return 0;
  });
});

// Filtered and sorted vendors
const filteredVendors = computed(() => {
  let filtered = vendors.value;

  // Apply search filter
  if (vendorSearch.value) {
    const search = vendorSearch.value.toLowerCase();
    filtered = filtered.filter(vendor =>
      vendor.vendor_name?.toLowerCase().includes(search) ||
      vendor.vendor_vat?.toLowerCase().includes(search) ||
      vendor.vendor_address?.toLowerCase().includes(search)
    );
  }

  // Apply sorting
  return filtered.sort((a, b) => {
    if (vendorOrderBy.value === 'vendor_name') {
      return (a.vendor_name || '').localeCompare(b.vendor_name || '');
    } else if (vendorOrderBy.value === 'vendor_vat') {
      return (a.vendor_vat || '').localeCompare(b.vendor_vat || '');
    } else if (vendorOrderBy.value === 'created_at') {
      return new Date(b.created_at) - new Date(a.created_at);
    }
    return 0;
  });
});

const filterCompanies = () => {
  // Trigger reactivity - computed property will handle it
};

const filterVendors = () => {
  // Trigger reactivity - computed property will handle it
};

const closeModal = () => {
  emit('close');
};

const selectCompany = (company) => {
  emit('select', { type: 'company', data: company });
};

const selectVendor = (vendor) => {
  emit('select', { type: 'vendor', data: vendor });
};

const loadCompanies = async () => {
  loadingCompanies.value = true;
  try {
    const response = await api.get('/api/companies', {
      params: {
        order_by: companyOrderBy.value,
        order_direction: 'asc'
      }
    });
    companies.value = response.data?.data || response.data || [];
  } catch (error) {
    console.error('Failed to load companies:', error);
    companies.value = [];
  } finally {
    loadingCompanies.value = false;
  }
};

const loadVendorsForCompany = async () => {
  loadingVendors.value = true;
  try {
    if (selectedCompanyId.value) {
      // Load vendors for specific company
      const response = await api.get(`/api/companies/${selectedCompanyId.value}/vendors`, {
        params: {
          order_by: vendorOrderBy.value,
          order_direction: 'asc'
        }
      });
      vendors.value = response.data?.data || response.data || [];
    } else {
      // Load all vendors from all companies
      const allVendorsPromises = companies.value.map(company =>
        api.get(`/api/companies/${company.id}/vendors`, {
          params: {
            order_by: vendorOrderBy.value,
            order_direction: 'asc'
          }
        }).catch(() => ({ data: [] }))
      );
      const vendorsResponses = await Promise.all(allVendorsPromises);
      vendors.value = vendorsResponses.flatMap((r, index) => {
        const vendorsList = r.data?.data || r.data || [];
        // Add company reference to each vendor
        return vendorsList.map(v => ({
          ...v,
          company: companies.value[index]
        }));
      });
    }
  } catch (error) {
    console.error('Failed to load vendors:', error);
    vendors.value = [];
  } finally {
    loadingVendors.value = false;
  }
};

// Load data when modal opens
watch(() => props.show, (newVal) => {
  if (newVal) {
    loadCompanies();
    loadVendorsForCompany();
  }
});

// Reload vendors when company filter changes or tab changes
watch(activeTab, (newTab) => {
  if (newTab === 'vendors' && vendors.value.length === 0) {
    loadVendorsForCompany();
  }
});

/* What each field offers, as a list: the fields are the app's own
   dropdowns now, so the options are data rather than tags. */
const companyOrderByOptions = computed(() => [
  { value: 'name', label: 'Alphabetically' },
  { value: 'created_at', label: 'Recently Added' },
  { value: 'vat_number', label: 'VAT Number' },
]);
const selectedCompanyIdOptions = computed(() => [
  { value: null, label: 'All Companies' },
  ...(unref(companies) || []).map((company) => ({ value: company.id, label: company.name })),
]);
const vendorOrderByOptions = computed(() => [
  { value: 'vendor_name', label: 'Alphabetical' },
  { value: 'created_at', label: 'Recent' },
  { value: 'vendor_vat', label: 'VAT' },
]);
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .relative,
.modal-leave-active .relative {
  transition: transform 0.3s ease;
}

.modal-enter-from .relative,
.modal-leave-to .relative {
  transform: scale(0.9);
}

/* ── The dashboard's dress ─────────────────────────────────────────────
 *
 * One card, one 12px radius, hairline edges, and controls at the table's
 * scale: 13px labels in 32px boxes, one filled button and the rest quiet.
 * A dialogue is opened from the table and should read as the same product.
 *
 * Last in the file on purpose — these do not delete what the modal set for
 * itself above, they settle the frame, the heading and the buttons. */
.modal-content,
.modal-container,
.modal-content-large {
    border: 1px solid #dde3ea;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
}

.modal-header {
    padding: 12px 14px;
    border-bottom: 1px solid #dde3ea;
    background: #fff;
}

.modal-header h2,
.modal-header h3,
.modal-header h4,
.modal-title {
    margin: 0;
    font-size: 14px;
    font-weight: 700;
    color: #1e293b;
}

.modal-subtitle {
    margin: 1px 0 0;
    font-size: 11px;
    font-weight: 400;
    color: #64748b;
}

.modal-body {
    padding: 12px 14px;
}

.modal-footer {
    padding: 10px 14px;
    gap: 8px;
    background: #fff;
    border-top: 1px solid #dde3ea;
}

/* The close: a mark on its own, with the lift the rest of the app has. */
.modal-header .btn-icon,
.btn-close,
.modal-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    min-width: 26px;
    padding: 0;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 8px;
    color: #64748b;
    box-shadow: none;
    transition: all 0.15s ease;
}

.modal-header .btn-icon:hover,
.btn-close:hover,
.modal-close:hover {
    background: transparent;
    color: #0697e4;
    transform: translateY(-2px);
}

/* The buttons: the same box everywhere, filled only where something is
   being committed. */
.btn-primary,
.btn-secondary,
.btn-cancel,
.btn-cancel-edit,
.btn-save,
.btn-save-edit,
.btn-confirm,
.btn-danger,
.btn-success,
.btn-add {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    height: 32px;
    padding: 0 14px;
    border-radius: 8px;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    line-height: 1;
    box-shadow: none;
    transition: all 0.15s ease;
}

.btn-secondary,
.btn-cancel,
.btn-cancel-edit {
    background: #fff;
    border: 1px solid #dde3ea;
    color: #1e293b;
}

.btn-secondary:hover:not(:disabled),
.btn-cancel:hover:not(:disabled),
.btn-cancel-edit:hover:not(:disabled) {
    background: #fff;
    border-color: #0697e4;
    color: #0697e4;
    box-shadow: none;
    transform: none;
}

.btn-primary,
.btn-confirm,
.btn-save,
.btn-save-edit,
.btn-add {
    background: #0697e4;
    border: 1px solid #0697e4;
    color: #fff;
}

.btn-primary:hover:not(:disabled),
.btn-confirm:hover:not(:disabled),
.btn-save:hover:not(:disabled),
.btn-save-edit:hover:not(:disabled),
.btn-add:hover:not(:disabled) {
    background: #0b84c7;
    border-color: #0b84c7;
    box-shadow: none;
    transform: none;
}

.btn-success {
    background: #10b981;
    border: 1px solid #10b981;
    color: #fff;
}

.btn-success:hover:not(:disabled) {
    background: #0ea371;
    border-color: #0ea371;
    box-shadow: none;
    transform: none;
}

.btn-danger {
    background: #e04545;
    border: 1px solid #e04545;
    color: #fff;
}

.btn-danger:hover:not(:disabled) {
    background: #c93a3a;
    border-color: #c93a3a;
    box-shadow: none;
    transform: none;
}

/* ── Fields at the table's scale ───────────────────────────────────────
 *
 * The dialogues were built with 16px type in 44px boxes, which next to the
 * 13px rows of the table reads as a different application. Same hairline,
 * same 8px corner, same type size as the controls on the dashboard. */
.modal-body input[type="text"],
.modal-body input[type="number"],
.modal-body input[type="email"],
.modal-body input[type="tel"],
.modal-body input[type="url"],
.modal-body input[type="search"],
.modal-body input[type="date"],
.modal-body input[type="password"],
.modal-body select,
.modal-body textarea {
    padding: 7px 10px;
    border: 1px solid #dde3ea;
    border-radius: 8px;
    font-family: inherit;
    font-size: 13px;
    color: #1e293b;
    background: #fff;
    box-shadow: none;
}

.modal-body input[type="text"]:focus,
.modal-body input[type="number"]:focus,
.modal-body input[type="email"]:focus,
.modal-body input[type="tel"]:focus,
.modal-body input[type="url"]:focus,
.modal-body input[type="search"]:focus,
.modal-body input[type="date"]:focus,
.modal-body input[type="password"]:focus,
.modal-body select:focus,
.modal-body textarea:focus {
    outline: none;
    border-color: #0697e4;
    box-shadow: none;
}

.modal-body label,
.modal-body .form-label {
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
}

/* A field with a mark standing in it keeps the room for the mark: the
   tighter padding above would have put the text under the magnifier. */
.search-box input,
.search-input,
.modal-search-input-wrapper input {
    padding-left: 30px;
}

/* Outweighing the field's own padding, which is written as .search-box
   .search-input and so outranks a plain descendant rule. */
.search-box input.search-input,
.modal-search-input-wrapper input {
    padding-left: 32px;
}

/* The list of counterparties, at the table's scale.
 *
 * Each one was a card 96px tall with a blue tile for a mark; twelve of them
 * made a column of boxes inside a box. They are rows now, parted by a
 * hairline, with the mark drawn in line and the address under the name. */
.modal-header .modal-title {
    font-size: 14px;
    font-weight: 700;
}

.modal-tabs {
    padding: 0 14px;
    border-bottom: 1px solid #dde3ea;
}

.modal-tab {
    height: 34px;
    padding: 0 10px;
    font-size: 13px;
    font-weight: 600;
}

.modal-search-container {
    padding: 10px 14px;
    gap: 8px;
}

.modal-search-container select,
.modal-search-input-wrapper input {
    height: 32px;
    padding: 0 10px;
    border: 1px solid #dde3ea;
    border-radius: 8px;
    font-size: 13px;
    background: #fff;
}

.modal-search-input-wrapper input {
    padding-left: 32px;
}

.modal-search-icon {
    color: #94a3b8;
}

.modal-list-item {
    display: flex;
    align-items: flex-start;
    gap: 9px;
    padding: 8px 14px;
    margin: 0;
    background: #fff;
    border: none;
    border-bottom: 1px solid #f1f5f9;
    border-radius: 0;
    box-shadow: none;
}

.modal-list-item:hover {
    background: #fbfcfd;
    border-color: #f1f5f9;
    transform: none;
    box-shadow: none;
}

.modal-list-item-icon {
    width: auto;
    height: auto;
    min-width: 0;
    padding: 0;
    margin-top: 2px;
    background: transparent;
    border-radius: 0;
    color: #0697e4;
    box-shadow: none;
}

.modal-list-item-icon svg {
    width: 14px;
    height: 14px;
}

.modal-list-item-title {
    font-size: 13px;
    font-weight: 700;
    color: #1e293b;
}

.modal-list-item-badge {
    padding: 1px 5px;
    font-size: 10px;
    font-weight: 600;
    border-radius: 4px;
}

.modal-list-item-address {
    display: flex;
    align-items: center;
    gap: 4px;
    margin: 1px 0 0;
    font-size: 11px;
    color: #64748b;
}

.modal-list-item-address svg {
    width: 12px;
    height: 12px;
}

.modal-list-item-arrow {
    color: #cbd5e1;
}

.modal-list-item-arrow svg {
    width: 13px;
    height: 13px;
}

/* The corners.
 *
 * The card is rounded but does not clip, so the header's own white — and the
 * footer's — was painted square and stood a pixel or two outside the curve.
 * The bands carry the same radius on the side they sit on. Clipping the card
 * instead would cut off anything a dialogue drops below its edge. */
.modal-header {
    border-radius: 12px 12px 0 0;
}

.modal-footer,
.modal-actions {
    border-radius: 0 0 12px 12px;
}

/* ── One hover for a mark on its own ───────────────────────────────────
 *
 * Wherever a button is just an icon — a row's edit or delete, a field's
 * helper, a dialogue's close — hovering turns the mark its own colour and
 * lifts it two pixels. Nothing is drawn behind it. Written last, and the
 * danger variant written with both of its classes, because the rules being
 * answered are themselves .btn-icon.btn-icon-danger. */
.btn-icon:hover:not(:disabled) {
  background: transparent;
  border-color: transparent;
  color: var(--primary, #0697e4);
  box-shadow: none;
  transform: translateY(-2px);
}

.btn-icon.btn-icon-danger:hover,
.btn-icon.btn-icon-danger:hover:not(:disabled) {
  background: transparent;
  border-color: transparent;
  color: #e04545;
  box-shadow: none;
  transform: translateY(-2px);
}

.btn-icon svg {
  transition: transform 0.15s ease;
}

/* ── One frame, not two ────────────────────────────────────────────────
 *
 * The chrome above gives a hairline, a 12px corner and a shadow to both
 * .modal-content and .modal-container — but in this dialogue the container
 * is only the box that centres the card, and in one case it is far wider
 * than the card, so its edge was drawn as a rectangle standing in the empty
 * space beside the dialogue. The card keeps the frame; the wrapper carries
 * none. */
.modal-container {
  background: transparent;
  border: none;
  border-radius: 0;
  box-shadow: none;
}
</style>